import type { PageNode, SectionNode, SectionOf, SectionType, SiteInfo, WebsiteDefinition } from "@baze/schema";
import type { ReactNode } from "react";
import type { BlogCardData } from "./blog";
import type { NavLink } from "./nav";
import type { StoreProductCard } from "./store";
import type { EngineTheme } from "./theme";

export type RenderMode = "live" | "editor";

/** Everything a section component may read. Plain data: safe to build on the server. */
export interface RenderContext {
  definition: WebsiteDefinition;
  site: SiteInfo;
  page: PageNode;
  theme: EngineTheme;
  mode: RenderMode;
  editor: boolean;
  nav: NavLink[];
  /** Content paths the section being rendered styles (section.style.texts); set per section by renderSection. */
  textKeys?: ReadonlySet<string>;
  /** Where the logo and "Beranda" go: "/" on multi-page sites and blog pages, else "#" (top of the page). */
  homeHref: string;
  /** Section whose title renders as the page's single <h1>. */
  h1SectionId: string | null;
  /** Section whose first image loads eagerly with fetchpriority="high" (LCP). */
  priorityImageSectionId: string | null;
  /** Anchors that exist on this page (visible sections), for in-page link fallbacks. */
  anchors: ReadonlySet<string>;
  siteId?: string;
  selectedSectionId?: string | null;
  hoveredSectionId?: string | null;
  /** Prefix for DOM ids, so several renders (thumbnails) can share one document. */
  idPrefix: string;
  /** Non-interactive render (thumbnails): no eager images. */
  thumbnail: boolean;
  /** Rendered inside a device frame (editor, draft preview): floating chrome pins to the frame, not the window. */
  framed: boolean;
  year: number;
  /** Render time (ms since epoch) for time-relative content such as countdowns. */
  now: number;
  /** Newest published blog posts, for the blog-latest section (the runtime fetches them; not in the definition). */
  posts?: BlogCardData[];
  /** Active products from the site's Toko (the runtime fetches them; not in the definition). */
  products?: StoreProductCard[];
  /** The Toko takes payments: "Beli" goes to checkout. Otherwise store sections order via WhatsApp. */
  store?: boolean;
}

export interface SectionProps<T extends SectionType = SectionType> {
  node: SectionOf<T>;
  ctx: RenderContext;
}

export type SectionComponent<T extends SectionType = SectionType> = (props: SectionProps<T>) => ReactNode;

export type AnySection = SectionNode;

/** `data-field` attributes for editor double-click → inspector focus. Empty in live mode. */
/**
 * Attributes for a piece of content text: `data-field` (editor: click to edit) and, when the section
 * styles that text, `data-tf` (this exact path) or `data-tg` (every item: indices as `*`).
 */
export function field(ctx: RenderContext, path: string): { "data-field"?: string; "data-tf"?: string; "data-tg"?: string } {
  const out: { "data-field"?: string; "data-tf"?: string; "data-tg"?: string } = ctx.editor ? { "data-field": path } : {};
  const keys = ctx.textKeys;
  if (keys) {
    if (keys.has(path)) out["data-tf"] = path;
    const generic = path.replace(/\.\d+(?=\.|$)/g, ".*");
    if (generic !== path && keys.has(generic)) out["data-tg"] = generic;
  }
  return out;
}

export function domId(ctx: RenderContext, id: string): string {
  return `${ctx.idPrefix}${id}`;
}

export function titleId(ctx: RenderContext, node: { id: string }): string {
  return domId(ctx, `${node.id}-title`);
}

export function messageVars(ctx: RenderContext, produk?: string) {
  return { nama_usaha: ctx.site.name, ...(produk ? { produk } : {}) };
}
